/**
 * Unified Order Types for OrderComplete and OrderLookup
 * Phase 2.1: Frontend Interface Harmonization
 *
 * This file provides harmonized interfaces that support both:
 * - OrderComplete (checkout success page)
 * - OrderLookup (order tracking page)
 *
 * With clean pricing structure and backward compatibility
 */

// =============================================================================
// CLEAN ORDER PRICING INTERFACES
// =============================================================================

export interface CleanOrderProductPricing {
  originalUnitPrice: number;
  originalTotal: number;
  customizationSurcharge: number;
  customizationTotal: number;
  discounts: {
    promotional?: {
      amountPerUnit: number;
      totalAmount: number;
      description: string;
      promotionId: number;
    };
    coupon?: {
      amountPerUnit: number;
      totalAmount: number;
      code: string;
      description: string;
    };
  };
  finalUnitPrice: number;
  finalTotal: number;
}

export interface CleanOrderProduct {
  id: number;
  quantity: number;
  cartCustomId?: string; // Optional for orders (not always preserved)

  // Clean pricing structure
  pricing: CleanOrderProductPricing;

  // Legacy pricing fields for backward compatibility
  price: number; // Maps to pricing.originalUnitPrice
  finalPrice: number; // Maps to pricing.finalUnitPrice

  product: {
    id: number;
    name: string;
    url?: string;
    images: Array<{
      thumbnail: string;
      small: string;
      medium?: string | null;
    }>;
    sku?: string;
    stock?: number;
  };

  // Additional order-specific fields
  customizableOptions?: any[];
  groupingAttributes?: any[];
  notes?: string;
  deliveryTimeRange?: string;
  itemStatus?: string;
}

export interface CleanOrderGroupTotals {
  originalSubtotal: number;
  customizationTotal: number;
  promotionalDiscountTotal: number;
  couponDiscountTotal: number;
  finalSubtotal: number;
  deliveryFeeTotal: number;
  groupTotal: number;
}

export interface CleanOrderGroup {
  id: number;
  deliveryMethod: string;
  deliveryDate: string;
  deliveryFee: number;

  // Clean group pricing
  groupTotals: CleanOrderGroupTotals;

  // Legacy fields for backward compatibility
  subTotal: number; // Maps to groupTotals.finalSubtotal
  groupTotal: number; // Maps to groupTotals.groupTotal

  items: CleanOrderProduct[];

  // Additional group fields
  groupStatus?: string;
  groupDeliveryStatus?: string;
  estimatedDeliveryDate?: string;
  actualDeliveryDate?: string;
}

export interface CleanOrderTotals {
  originalSubtotal: number;
  customizationTotal: number;
  promotionalDiscountTotal: number;
  couponDiscountTotal: number;
  deliveryFeeTotal: number;
  finalSubtotal: number;
  grandTotal: number;
}

export interface CleanOrderCoupon {
  code: string;
  description: string;
  totalDiscount: number;
}

export interface CleanOrderPromotion {
  id: number;
  name: string;
  description: string;
  totalDiscount: number;
}

// =============================================================================
// UNIFIED ORDER INTERFACE
// =============================================================================

interface UserAddress {
  id?: number;
  street?: string;
  streetNumber?: string;
  district?: string;
  reference?: string;
  zipCode?: string;
  city?: string;
  state?: string;
  country?: string;
}

interface ReceiverInfo {
  name?: string;
  phone?: string;
  documentType?: string;
  documentNumber?: string;
}

interface CustomerInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  documentType?: string;
  documentNumber?: string;
}

export interface UnifiedOrderData {
  // Basic order identification - harmonized to string
  orderId: string; // Harmonized from number (OrderComplete) and string (OrderLookup)
  generatedOrderId: string;
  createdAt: string;
  orderDate?: string; // Optional for backward compatibility with OrderLookup

  // Customer information
  customerName: string;
  email?: string; // Optional - present in OrderComplete, missing in OrderLookup

  // Payment information
  paymentMethod: string;
  paymentStatus: "paid" | "pending";
  orderStatus: "processing" | "shipped" | "delivered";

  // Clean pricing structure (new)
  totals: CleanOrderTotals;
  appliedCoupons: CleanOrderCoupon[];
  appliedPromotions: CleanOrderPromotion[];

  // Legacy totals for backward compatibility
  totalPrice: number; // Maps to totals.finalSubtotal
  totalDeliveryFee: number; // Maps to totals.deliveryFeeTotal
  finalTotal: number; // Maps to totals.grandTotal

  // Order groups with clean pricing
  groups: CleanOrderGroup[];

  // Additional order fields
  orderComments?: string;
  trackingNumber?: string;
  estimatedDeliveryDate?: string;
  actualDeliveryDate?: string;

  // **NEW: Add shipping information interfaces**
  user_address?: UserAddress;
  receiverInfo?: ReceiverInfo;
  customerInfo?: CustomerInfo;
}

// =============================================================================
// LEGACY INTERFACES (for backward compatibility during migration)
// =============================================================================

// Legacy OrderComplete interface (to be gradually replaced)
export interface LegacyOrderState {
  orderId: number;
  generatedOrderId: string;
  createdAt: string;
  email: string;
  customerName: string;
  paymentStatus: "paid" | "pending";
  paymentMethod: "yapePlin" | "money_transfer" | "payme";
  totalPrice: number;
  totalDeliveryFee: number;
  finalTotal: number;
  groups: Array<{
    id: number;
    subTotal: number;
    deliveryMethod: string;
    deliveryDate: string;
    deliveryFee: number;
    groupTotal: number;
    items: Array<{
      id: number;
      quantity: number;
      price: number;
      finalPrice: number;
      product: {
        id: number;
        name: string;
        images: {
          thumbnail: string;
          small: string;
          medium: string;
        }[];
      };
    }>;
  }>;
  orderStatus: "processing" | "shipped" | "delivered";
}

// Legacy OrderLookup interface (to be gradually replaced)
export interface LegacyOrderData {
  orderId: string;
  createdAt: string;
  orderDate: string;
  generatedOrderId: string;
  customerName: string;
  totalPrice: number;
  totalDeliveryFee: number;
  finalTotal: number;
  paymentMethod: string;
  groups: Array<{
    id: number;
    deliveryMethod: string;
    deliveryDate: string;
    subTotal: number;
    deliveryFee: number;
    groupTotal: number;
    items: Array<{
      id: number;
      quantity: number;
      price: number;
      finalPrice: number;
      product: {
        id: number;
        name: string;
        images: {
          thumbnail: string;
          small: string;
          medium: string | null;
        }[];
      };
    }>;
  }>;
  orderStatus: "processing" | "shipped" | "delivered";
  paymentStatus: "pending" | "paid";
}

// =============================================================================
// UTILITY TYPES AND FUNCTIONS
// =============================================================================

/**
 * Transform legacy order data to unified format
 * This helps during the migration phase
 */
export function transformLegacyToUnified(
  legacyOrder: LegacyOrderState | LegacyOrderData
): UnifiedOrderData {
  // Handle orderId type differences
  const orderId =
    typeof legacyOrder.orderId === "number"
      ? legacyOrder.orderId.toString()
      : legacyOrder.orderId;

  return {
    orderId,
    generatedOrderId: legacyOrder.generatedOrderId,
    createdAt: legacyOrder.createdAt,
    orderDate:
      "orderDate" in legacyOrder
        ? legacyOrder.orderDate
        : legacyOrder.createdAt,
    customerName: legacyOrder.customerName,
    email: "email" in legacyOrder ? legacyOrder.email : undefined,
    paymentMethod: legacyOrder.paymentMethod,
    paymentStatus: legacyOrder.paymentStatus,
    orderStatus: legacyOrder.orderStatus,

    // Create default clean totals structure
    totals: {
      originalSubtotal: legacyOrder.totalPrice,
      customizationTotal: 0, // Unknown in legacy data
      promotionalDiscountTotal: 0, // Unknown in legacy data
      couponDiscountTotal: 0, // Unknown in legacy data
      deliveryFeeTotal: legacyOrder.totalDeliveryFee,
      finalSubtotal: legacyOrder.totalPrice,
      grandTotal: legacyOrder.finalTotal,
    },

    // Default empty arrays for new structure
    appliedCoupons: [],
    appliedPromotions: [],

    // Legacy totals for compatibility
    totalPrice: legacyOrder.totalPrice,
    totalDeliveryFee: legacyOrder.totalDeliveryFee,
    finalTotal: legacyOrder.finalTotal,

    // Transform groups
    groups: legacyOrder.groups.map((group) => ({
      id: group.id,
      deliveryMethod: group.deliveryMethod,
      deliveryDate: group.deliveryDate,
      deliveryFee: group.deliveryFee,

      // Create default group totals
      groupTotals: {
        originalSubtotal: group.subTotal,
        customizationTotal: 0,
        promotionalDiscountTotal: 0,
        couponDiscountTotal: 0,
        finalSubtotal: group.subTotal,
        deliveryFeeTotal: group.deliveryFee,
        groupTotal: group.groupTotal,
      },

      // Legacy fields
      subTotal: group.subTotal,
      groupTotal: group.groupTotal,

      // Transform items
      items: group.items.map((item) => ({
        id: item.id,
        quantity: item.quantity,

        // Create default pricing structure
        pricing: {
          originalUnitPrice: item.price,
          originalTotal: item.price * item.quantity,
          customizationSurcharge: 0,
          customizationTotal: 0,
          discounts: {},
          finalUnitPrice: item.finalPrice,
          finalTotal: item.finalPrice * item.quantity,
        },

        // Legacy fields
        price: item.price,
        finalPrice: item.finalPrice,

        product: item.product,
      })),
    })),
  };
}

/**
 * Type guard to check if order data has clean pricing structure
 */
export function hasCleanPricing(orderData: any): orderData is UnifiedOrderData {
  return (
    orderData &&
    orderData.totals &&
    typeof orderData.totals.originalSubtotal === "number" &&
    Array.isArray(orderData.appliedCoupons) &&
    Array.isArray(orderData.appliedPromotions) &&
    orderData.groups &&
    orderData.groups.length > 0 &&
    orderData.groups[0].groupTotals &&
    typeof orderData.groups[0].groupTotals.originalSubtotal === "number"
  );
}

/**
 * Helper to safely get clean pricing data or fallback to legacy
 */
export function getOrderTotals(
  orderData: UnifiedOrderData | LegacyOrderState | LegacyOrderData
) {
  if (hasCleanPricing(orderData)) {
    return {
      subtotal: orderData.totals.finalSubtotal,
      deliveryFee: orderData.totals.deliveryFeeTotal,
      discounts: {
        promotional: orderData.totals.promotionalDiscountTotal,
        coupon: orderData.totals.couponDiscountTotal,
        total:
          orderData.totals.promotionalDiscountTotal +
          orderData.totals.couponDiscountTotal,
      },
      grandTotal: orderData.totals.grandTotal,
    };
  }

  // Fallback to legacy structure
  return {
    subtotal: orderData.totalPrice,
    deliveryFee: orderData.totalDeliveryFee,
    discounts: {
      promotional: 0,
      coupon: 0,
      total: 0,
    },
    grandTotal: orderData.finalTotal,
  };
}

// Type exports for easier importing
export type OrderData = UnifiedOrderData;
export type OrderState = UnifiedOrderData;
export type OrderProduct = CleanOrderProduct;
export type OrderGroup = CleanOrderGroup;
